/**
 * Redux 数据量限制工具
 * 
 * 用于防止 Redux state 中数组数据过大导致性能问题
 * 支持多种限制策略，可应用到任何 Redux slice
 * 
 * @example
 * // 在 reducer 中使用
 * import { limitArray } from '@/redux/utils/dataLimitHelper'
 * 
 * addItem: (state, action) => {
 *   const newArray = [...state.data.list, action.payload]
 *   state.data.list = limitArray(newArray, { maxLength: 1000, strategy: 'fifo' })
 * }
 */

/**
 * 限制策略类型
 * - 'fifo': 先进先出，保留最新的 N 条（从末尾截取）
 * - 'lifo': 后进先出，保留最旧的 N 条（从开头截取）
 * - 'time': 按时间戳字段过滤，保留最近 N 小时/天的数据
 * - 'size': 按数据大小估算，保留不超过指定内存大小的数据
 */
const LIMIT_STRATEGIES = {
  FIFO: 'fifo',
  LIFO: 'lifo',
  TIME: 'time',
  SIZE: 'size'
}

/**
 * 限制数组长度，防止数据量过大
 * 
 * @param {Array} array - 要限制的数组
 * @param {Object} options - 限制配置
 * @param {number} options.maxLength - 最大长度（必填）
 * @param {string} options.strategy - 限制策略，默认 'fifo'
 * @param {string} options.timeField - 时间字段名（strategy='time' 时必填）
 * @param {number} options.timeRange - 时间范围（小时，strategy='time' 时必填）
 * @param {number} options.maxSizeKB - 最大内存大小（KB，strategy='size' 时必填）
 * @param {Function} options.getItemSize - 计算单个元素大小的函数（strategy='size' 时必填）
 * @returns {Array} 限制后的数组
 * 
 * @example
 * // FIFO 策略：保留最新的 1000 条
 * limitArray(messages, { maxLength: 1000, strategy: 'fifo' })
 * 
 * // TIME 策略：保留最近 24 小时的数据
 * limitArray(alarms, { 
 *   maxLength: 10000, 
 *   strategy: 'time', 
 *   timeField: 'createTime', 
 *   timeRange: 24 
 * })
 */
export const limitArray = (array = [], options = {}) => {
  if (!Array.isArray(array)) {
    console.warn('[dataLimitHelper] limitArray: 输入不是数组，返回空数组')
    return []
  }

  const {
    maxLength = 1000,
    strategy = LIMIT_STRATEGIES.FIFO
  } = options

  // 如果数组长度未超过限制，直接返回
  if (array.length <= maxLength) {
    return array
  }

  switch (strategy) {
    case LIMIT_STRATEGIES.FIFO:
      // 先进先出：保留最新的 N 条（从末尾截取）
      return array.slice(-maxLength)

    case LIMIT_STRATEGIES.LIFO:
      // 后进先出：保留最旧的 N 条（从开头截取）
      return array.slice(0, maxLength)

    case LIMIT_STRATEGIES.TIME: {
      // 按时间过滤：保留最近 N 小时的数据
      const { timeField = 'createTime', timeRange = 24 } = options
      const now = Date.now()
      const timeThreshold = now - (timeRange * 60 * 60 * 1000) // 转换为毫秒

      const filtered = array.filter(item => {
        const timeValue = item?.[timeField]
        if (!timeValue) return false

        // 支持时间戳（毫秒或秒）和 Date 对象
        const timestamp = typeof timeValue === 'number'
          ? (timeValue < 1e12 ? timeValue * 1000 : timeValue) // 秒转毫秒
          : new Date(timeValue).getTime()

        return timestamp >= timeThreshold
      })

      // 如果按时间过滤后仍超过限制，再使用 FIFO 策略
      return filtered.length > maxLength
        ? filtered.slice(-maxLength)
        : filtered
    }

    case LIMIT_STRATEGIES.SIZE: {
      // 按内存大小估算：保留不超过指定大小的数据
      const { maxSizeKB = 1024, getItemSize } = options

      if (typeof getItemSize !== 'function') {
        console.warn('[dataLimitHelper] limitArray: SIZE 策略需要提供 getItemSize 函数，回退到 FIFO 策略')
        return array.slice(-maxLength)
      }

      let totalSize = 0
      const maxSizeBytes = maxSizeKB * 1024
      const result = []

      // 从后往前遍历，保留最新的数据直到达到大小限制
      for (let i = array.length - 1; i >= 0; i--) {
        const item = array[i]
        const itemSize = getItemSize(item) || 0

        if (totalSize + itemSize <= maxSizeBytes && result.length < maxLength) {
          result.unshift(item) // 保持顺序
          totalSize += itemSize
        } else {
          break
        }
      }

      return result
    }

    default:
      console.warn(`[dataLimitHelper] limitArray: 未知的策略 "${strategy}"，使用默认 FIFO 策略`)
      return array.slice(-maxLength)
  }
}

/**
 * 创建带限制的 reducer 函数
 * 用于包装现有的 reducer，自动应用数据限制
 * 
 * @param {Function} originalReducer - 原始 reducer 函数
 * @param {Object} limitConfig - 限制配置，格式：{ statePath: { maxLength, strategy, ... } }
 * @returns {Function} 包装后的 reducer
 * 
 * @example
 * const limitedReducer = createLimitedReducer(
 *   mySlice.reducer,
 *   {
 *     'data.messages': { maxLength: 1000, strategy: 'fifo' },
 *     'data.alarms': { maxLength: 5000, strategy: 'time', timeField: 'alarmTime', timeRange: 24 }
 *   }
 * )
 */
export const createLimitedReducer = (originalReducer, limitConfig = {}) => {
  return (state, action) => {
    // 先执行原始 reducer
    const newState = originalReducer(state, action)

    // 对配置的路径应用限制
    Object.keys(limitConfig).forEach(path => {
      const config = limitConfig[path]
      const keys = path.split('.')
      let target = newState

      // 导航到目标对象
      for (let i = 0; i < keys.length - 1; i++) {
        if (!target || typeof target !== 'object') {
          return // 路径不存在，跳过
        }
        target = target[keys[i]]
      }

      // 应用限制
      const lastKey = keys[keys.length - 1]
      if (target && Array.isArray(target[lastKey])) {
        target[lastKey] = limitArray(target[lastKey], config)
      }
    })

    return newState
  }
}

/**
 * 预设的常用配置
 * 可以直接使用或作为参考
 */
export const PRESET_CONFIGS = {
  // SSE 消息配置（保留最新 300 条）
  SSE_MESSAGES: {
    maxLength: 300,
    strategy: LIMIT_STRATEGIES.FIFO
  },

  // 告警列表配置（保留最新 5000 条，或最近 7 天的数据）
  ALARM_LIST: {
    maxLength: 5000,
    strategy: LIMIT_STRATEGIES.TIME,
    timeField: 'alarmTime',
    timeRange: 24 * 7 // 7 天
  },

  // 设备状态更新（保留最新 1000 条）
  DEVICE_STATUS: {
    maxLength: 1000,
    strategy: LIMIT_STRATEGIES.FIFO
  },

  // 通知消息（保留最新 500 条）
  NOTIFICATIONS: {
    maxLength: 500,
    strategy: LIMIT_STRATEGIES.FIFO
  }
}

/**
 * 估算对象大小的辅助函数（用于 SIZE 策略）
 * 
 * @param {*} obj - 要估算大小的对象
 * @returns {number} 估算的字节数
 */
export const estimateObjectSize = (obj) => {
  if (obj === null || obj === undefined) return 0

  // 简单估算：JSON 序列化后的字符串长度
  try {
    return JSON.stringify(obj).length
  } catch (e) {
    // 如果无法序列化，返回一个默认值
    return 100 // 假设每个对象至少 100 字节
  }
}

// 导出策略常量
export { LIMIT_STRATEGIES }
